import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import WaitlistSignup from '../components/WaitlistSignup';
import { Search, MapPin, Users, Bed, Bath, Star, Heart, Zap } from 'lucide-react';
import { propertyAPI } from '../services/api';
import { useAuth } from '../contexts/AuthContext';

const PropertyBrowsingPage = () => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCity, setSelectedCity] = useState('all');
  const [guests, setGuests] = useState(1);
  const [priceRange, setPriceRange] = useState('all');
  const [propertyType, setPropertyType] = useState('all');
  const [favorites, setFavorites] = useState([]);
  const [cities, setCities] = useState(['all']);
  const [accomStatus, setAccomStatus] = useState(null); // { live, hostCount, threshold, remaining }

  // Note: No redirect for logged-in users — let them see the landing page
  // and use the navigation buttons. This prevents back-button loops.

  // Fetch properties on mount and when filters change
  useEffect(() => {
    fetchProperties();
  }, [selectedCity, propertyType, priceRange, guests]);

  // Fetch accommodation gate status once on mount. Public endpoint, no auth.
  // Drives the "X of N hosts" banner shown above the grid until listings unlock
  // at the host threshold. The threshold is read from the API (currently 50),
  // never hardcoded, so it follows any admin change to the gate.
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    fetch(`${API_URL}/api/platform/accommodation-status`)
      .then(r => r.ok ? r.json() : null)
      .then(setAccomStatus)
      .catch(() => setAccomStatus(null));
  }, []);

  const fetchProperties = async () => {
    setLoading(true);
    try {
      const filters = {
        city: selectedCity,
        propertyType: propertyType,
        guests: guests
      };

      if (priceRange !== 'all') {
        if (priceRange === 'budget') {
          filters.maxPrice = 150;
        } else if (priceRange === 'mid') {
          filters.minPrice = 150;
          filters.maxPrice = 250;
        } else if (priceRange === 'luxury') {
          filters.minPrice = 250;
        }
      }

      const data = await propertyAPI.getPublishedProperties(filters);
      setProperties(data);

      // Extract unique cities
      const uniqueCities = ['all', ...new Set(data.map(p => p.location.city))];
      setCities(uniqueCities);
    } catch (error) {
      console.error('Error fetching properties:', error);
    } finally {
      setLoading(false);
    }
  };

  // Filter by search term (client-side)
  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         property.location.city.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const toggleFavorite = (propertyId) => {
    setFavorites(prev => 
      prev.includes(propertyId) 
        ? prev.filter(id => id !== propertyId)
        : [...prev, propertyId]
    );
  };

  return (
    <div className="min-h-screen bg-[#F4F2E9]">
      {/* Navigation Header */}
      <header className="bg-white shadow-sm sticky top-0 z-50">
        <div className="container mx-auto px-4">
          <nav className="flex items-center justify-between h-20">
            <Link to="/" className="flex items-center gap-3">
              <div className="h-[30px] w-auto flex items-center justify-center flex-shrink-0"><img src="/logo.png" alt="Ariv" className="h-[30px] w-auto object-contain" /></div>
            </Link>

            <div className="hidden md:flex items-center gap-8">
              <Link to="/" className="text-gray-700 hover:text-[#1F3A68] font-medium">
                Browse Properties
              </Link>
              {user?.role === 'host' && (
                <Link to="/host/dashboard" className="text-gray-700 hover:text-[#1F3A68] font-medium">
                  Host Dashboard
                </Link>
              )}
              <Link to="/services" className="text-gray-700 hover:text-[#1F3A68] font-medium">
                Find Services
              </Link>
              <Link to="/about" className="text-gray-700 hover:text-[#1F3A68] font-medium">
                About
              </Link>
            </div>

            <div className="flex items-center gap-3">
              {user ? (
                <>
                  {user.role === 'host' && (
                    <Button variant="outline" onClick={() => navigate('/host/dashboard')}>
                      Dashboard
                    </Button>
                  )}
                  {user.role === 'guest' && (
                    <Button variant="outline" onClick={() => navigate('/guest/bookings')}>
                      My Bookings
                    </Button>
                  )}
                  <Button onClick={() => { logout(); navigate('/'); }} className="bg-[#1F3A68] hover:bg-[#1F3A68]/90">
                    Logout
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => navigate('/login')}>
                    Log In
                  </Button>
                  <Button onClick={() => navigate('/signup')} className="bg-[#1F3A68] hover:bg-[#1F3A68]/90">
                    Sign Up
                  </Button>
                </>
              )}
            </div>
          </nav>
        </div>
      </header>

      {/* Hero Search Section */}
      <section className="bg-gradient-to-r from-[#1F3A68] to-[#1F3A68]/80 text-white py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-10">
            <h1 className="text-5xl font-bold mb-4">
              Find Your Perfect Stay
            </h1>
            <p className="text-xl text-white/90">
              Discover unique properties with exceptional hospitality
            </p>
          </div>

          {/* Search Bar */}
          <Card className="max-w-5xl mx-auto border-0 shadow-2xl">
            <CardContent className="p-6">
              <div className="grid md:grid-cols-4 gap-4">
                <div className="md:col-span-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      placeholder="Search by city or property name"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>
                
                <div>
                  <div className="relative">
                    <Users className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                    <Input
                      type="number"
                      min="1"
                      placeholder="Guests"
                      value={guests}
                      onChange={(e) => setGuests(parseInt(e.target.value) || 1)}
                      className="pl-10 h-12"
                    />
                  </div>
                </div>

                <Button className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90 h-12">
                  Search
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Filters & Results */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* Filter Bar */}
          <div className="bg-white rounded-xl shadow-md p-6 mb-8">
            <div className="grid md:grid-cols-4 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  <MapPin className="inline w-4 h-4 mr-1" />
                  City
                </label>
                <select
                  value={selectedCity}
                  onChange={(e) => setSelectedCity(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F3A68] focus:border-transparent"
                >
                  {cities.map(city => (
                    <option key={city} value={city}>
                      {city === 'all' ? 'All Cities' : city}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Property Type
                </label>
                <select
                  value={propertyType}
                  onChange={(e) => setPropertyType(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F3A68] focus:border-transparent"
                >
                  <option value="all">All Types</option>
                  <option value="Apartment">Apartment</option>
                  <option value="House">House</option>
                  <option value="Loft">Loft</option>
                  <option value="Studio">Studio</option>
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Price Range
                </label>
                <select
                  value={priceRange}
                  onChange={(e) => setPriceRange(e.target.value)}
                  className="w-full h-10 px-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#1F3A68] focus:border-transparent"
                >
                  <option value="all">All Prices</option>
                  <option value="budget">Under £150</option>
                  <option value="mid">£150 - £250</option>
                  <option value="luxury">Above £250</option>
                </select>
              </div>

              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedCity('all');
                    setPropertyType('all');
                    setPriceRange('all');
                    setSearchTerm('');
                  }}
                  className="w-full h-10"
                >
                  Clear Filters
                </Button>
              </div>
            </div>
          </div>

          {/* Results Count */}
          <div className="mb-6">
            <p className="text-gray-600 text-lg">
              {loading ? (
                'Loading properties...'
              ) : (
                <>
                  <span className="font-bold text-[#1F3A68]">{filteredProperties.length}</span> 
                  {filteredProperties.length === 0 ? ' No properties found' : ' properties found'}
                </>
              )}
            </p>
          </div>

          {/* Accommodation supply gate banner — only shown while listings are
              still locked (host count under the threshold). Disappears
              automatically once the gate opens. */}
          {accomStatus && !accomStatus.live && (
            <div className="mb-8 bg-gradient-to-r from-amber-50 to-amber-100 border border-amber-300 rounded-2xl p-6 md:p-8">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-6 justify-between">
                <div className="flex-1">
                  <div className="inline-flex items-center gap-2 bg-amber-200/60 text-amber-900 text-[10px] font-bold uppercase tracking-widest rounded-full px-2.5 py-1 mb-3">
                    <Zap size={12} /> Coming soon
                  </div>
                  <h2 className="text-2xl md:text-3xl font-black text-amber-900 leading-tight mb-2">
                    Stays unlock when {accomStatus.threshold} hosts have signed up
                  </h2>
                  <p className="text-amber-800/80 text-sm md:text-base leading-relaxed max-w-2xl">
                    We're building the founding host cohort before opening accommodation bookings, so when you book a stay there's already real choice. Food ordering and the services marketplace are switched on as we build up local supply, and taxis follow once our private hire operator licence is in place.
                  </p>
                  <div className="mt-4 flex items-center gap-4">
                    <div className="flex-1 max-w-md">
                      <div className="flex justify-between text-xs font-bold text-amber-900 mb-1.5">
                        <span>{accomStatus.hostCount} of {accomStatus.threshold} hosts</span>
                        <span>{accomStatus.remaining} to go</span>
                      </div>
                      <div className="h-2.5 bg-amber-200/70 rounded-full overflow-hidden">
                        <div
                          className="h-full bg-amber-500 rounded-full transition-all duration-700"
                          style={{ width: `${Math.min(100, (accomStatus.hostCount / accomStatus.threshold) * 100)}%` }}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  <Button
                    onClick={() => {
                      if (!user) return navigate('/signup');
                      const roles = user.roles || [];
                      if (roles.includes('host')) return navigate('/host/dashboard');
                      if (roles.includes('provider') || roles.includes('service_provider')) return navigate('/provider/dashboard');
                      if (roles.includes('landlord')) return navigate('/landlord/dashboard');
                      return navigate('/guest/bookings');
                    }}
                    className="bg-amber-600 hover:bg-amber-700 text-white font-bold px-6 py-3 rounded-xl shadow-md"
                  >
                    {user ? 'Get your referral link' : 'Sign up to refer hosts'}
                  </Button>
                  <p className="text-[11px] text-amber-700/80 mt-2 max-w-[180px] text-right">
                    Earn a share of the bookings your referred hosts take
                  </p>
                </div>
              </div>
            </div>
          )}


          {/* Waitlist, shown only while stays are still locked. The banner
              above explains why guests cannot book yet; this is where that
              demand gets captured instead of walking away. It records which
              side of the marketplace each person is waiting for, so the list
              is useful rather than being a pile of email addresses. */}
          {accomStatus && !accomStatus.live && (
            <div className="mb-10 max-w-2xl">
              <WaitlistSignup
                defaultRole="guest"
                heading="Tell us where to send the news"
                blurb="Leave your email and we will let you know the moment stays open. You will get a link of your own, and anyone who joins through it counts as your introduction."
              />
            </div>
          )}
          {/* Property Grid */}
          {loading ? (
            <div className="text-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1F3A68] mx-auto"></div>
              <p className="mt-4 text-gray-600">Loading properties...</p>
            </div>
          ) : filteredProperties.length === 0 ? (
            <div className="text-center py-20">
              <div className="text-gray-400 mb-4">
                <Search size={64} className="mx-auto" />
              </div>
              <h3 className="text-2xl font-bold text-gray-700 mb-2">No properties found</h3>
              <p className="text-gray-600 mb-6">
                {properties.length === 0 
                  ? 'No properties have been published yet. Check back soon!' 
                  : 'Try adjusting your filters or search terms'}
              </p>
              {user?.role === 'host' && properties.length === 0 && (
                <Button 
                  onClick={() => navigate('/host/create-property')}
                  className="bg-[#1F3A68] hover:bg-[#1F3A68]/90"
                >
                  Be the first to list a property
                </Button>
              )}
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
              {filteredProperties.map(property => (
                <Card
                  key={property._id}
                  className="overflow-hidden hover:shadow-2xl transition-all duration-300 cursor-pointer group"
                  onClick={() => navigate(`/property/${property._id}`)}
                >
                <div className="relative">
                  <img
                    src={property.images?.[0] || 'https://images.unsplash.com/photo-1522708323590-d24dbb6b0267?w=1200&h=800&fit=crop'}
                    alt={property.title}
                    className="w-full h-64 object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      toggleFavorite(property._id);
                    }}
                    className="absolute top-3 right-3 w-10 h-10 bg-white/90 rounded-full flex items-center justify-center hover:bg-white transition-colors"
                  >
                    <Heart
                      size={20}
                      className={favorites.includes(property._id) ? 'fill-red-500 text-red-500' : 'text-gray-600'}
                    />
                  </button>
                  {property.instantBook && (
                    <div className="absolute top-3 left-3 bg-[#1F3A68] text-white px-3 py-1 rounded-full text-sm font-semibold flex items-center gap-1">
                      <Zap size={14} />
                      Instant Book
                    </div>
                  )}
                </div>

                <CardContent className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h3 className="font-bold text-lg text-gray-900 mb-1 line-clamp-1">
                        {property.title}
                      </h3>
                      <p className="text-gray-600 text-sm flex items-center gap-1">
                        <MapPin size={14} />
                        {property.location?.city || 'Unknown'}
                      </p>
                    </div>
                    {property.rating > 0 && (
                      <div className="flex items-center gap-1 bg-[#1F3A68] text-white px-2 py-1 rounded-lg">
                        <Star size={14} fill="white" />
                        <span className="text-sm font-semibold">{property.rating}</span>
                      </div>
                    )}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                    <span className="flex items-center gap-1">
                      <Users size={16} />
                      {property.capacity?.guests || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bed size={16} />
                      {property.capacity?.bedrooms || 0}
                    </span>
                    <span className="flex items-center gap-1">
                      <Bath size={16} />
                      {property.capacity?.bathrooms || 0}
                    </span>
                  </div>

                  <div className="border-t pt-3">
                    <div className="flex items-end justify-between">
                      <div>
                        <span className="text-2xl font-bold text-[#1F3A68]">
                          £{property.pricing?.nightlyRate || 0}
                        </span>
                        <span className="text-gray-600 text-sm"> / night</span>
                      </div>
                      <Button
                        size="sm"
                        className="bg-[#CB3B3B] hover:bg-[#CB3B3B]/90"
                        onClick={(e) => {
                          e.stopPropagation();
                          navigate(`/property/${property._id}`);
                        }}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#1F3A68] text-white py-12 mt-20">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            <div>
              <div className="h-[46px] w-auto flex items-center justify-center flex-shrink-0 mb-4"><img src="/logo.png" alt="Ariv" className="h-[46px] w-auto object-contain brightness-0 invert" /></div>
              <p className="text-white/80">Your marketplace for UK stays</p>
            </div>
            <div>
              <h4 className="font-bold mb-4">For Guests</h4>
              <ul className="space-y-2 text-white/80">
                <li><Link to="/" className="hover:text-white">Browse Properties</Link></li>
                <li><Link to="/how-it-works" className="hover:text-white">How It Works</Link></li>
                <li><Link to="/help" className="hover:text-white">Help Center</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">For Hosts</h4>
              <ul className="space-y-2 text-white/80">
                <li><Link to="/become-host" className="hover:text-white">Become a Host</Link></li>
                <li><Link to="/host/calculator" className="hover:text-white">Profit Calculator</Link></li>
                <li><Link to="/host/resources" className="hover:text-white">Host Resources</Link></li>
                <li><Link to="/airbnb-host-fee-alternative/" className="hover:text-white">Lower fee than Airbnb</Link></li>
                <li><Link to="/low-commission-airbnb-management/" className="hover:text-white">Low commission management</Link></li>
                <li><Link to="/airbnb-cleaning/" className="hover:text-white">Cleaning by city</Link></li>
              </ul>
            </div>
            <div>
              <h4 className="font-bold mb-4">Company</h4>
              <ul className="space-y-2 text-white/80">
                <li><Link to="/about" className="hover:text-white">About Us</Link></li>
                <li><Link to="/careers" className="hover:text-white">Careers</Link></li>
                <li><Link to="/contact" className="hover:text-white">Contact</Link></li>
                <li><Link to="/trust" className="hover:text-white">Trust &amp; Safety</Link></li>
              </ul>
            </div>
          </div>
          <div className="border-t border-white/20 mt-8 pt-8 text-center text-white/60">
            <p>&copy; {new Date().getFullYear()} Ariv. A product of Brick &amp; Capital Ltd. All rights reserved.</p>
            <p className="text-xs mt-2 text-white/50"><strong>Brick &amp; Capital Ltd</strong> &middot; Registered in England and Wales, company number <a href="https://find-and-update.company-information.service.gov.uk/company/16655788" target="_blank" rel="noopener noreferrer" className="underline hover:text-white">16655788</a> &middot; Registered office: Bowness House, 815 Old Kent Road, London SE15 1NX</p>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default PropertyBrowsingPage;
